import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Questionnaire from '../components/Questionnaire';
import { useQuestionnaireContext } from '../context/QuestionnaireContext';

// ANKIETA 1 - Initial questionnaire configuration
const samplePreQuestionnaire = [
    // Pytanie 1 - Część 1: Przedział wiekowy
    {
        id: 'age',
        type: 'radio',
        question: 'Proszę podać przedział wiekowy',
        required: true,
        options: [
            { value: 'below-18', label: 'poniżej 18' },
            { value: '18-29', label: '18–29' },
            { value: '30-49', label: '30–49' },
            { value: '50-65', label: '50–65' },
            { value: 'above-65', label: 'powyżej 65' }
        ]
    },
    // Pytanie 1 - Część 2: Status zawodowy
    {
        id: 'employment_status',
        type: 'radio',
        question: 'Proszę podać status zawodowy',
        required: true,
        options: [
            { value: 'employed', label: 'pracuję' },
            { value: 'unemployed', label: 'nie pracuję' },
            { value: 'seeking', label: 'poszukuję pracy' }
        ]
    },
    // Pytanie 1 - Część 3: Poziom wykształcenia
    {
        id: 'education',
        type: 'radio',
        question: 'Proszę podać poziom wykształcenia',
        required: true,
        options: [
            { value: 'primary', label: 'podstawowe' },
            { value: 'vocational', label: 'zawodowe' },
            { value: 'secondary', label: 'średnie' },
            { value: 'higher', label: 'wyższe' }
        ]
    },
    // Pytanie 1 - Część 4: Płeć
    {
        id: 'gender',
        type: 'radio',
        question: 'Proszę określić płeć',
        required: false,
        options: [
            { value: 'female', label: 'kobieta' },
            { value: 'male', label: 'mężczyzna' },
            { value: 'skip', label: 'pomiń odpowiedź' }
        ]
    },
    // Pytanie 2 - Część 1: Aktywność towarzyska
    {
        id: 'social_activity',
        type: 'slider',
        question: 'Aktywność towarzyska to spotkania, rozmowy czy wspólne zajęcia, które pozwalają budować relacje i czuć się częścią grupy.',
        description: 'Oceń na skali 1–10, jak często angażujesz się w aktywności towarzyskie, gdzie 1 oznacza prawie nigdy, a 10 bardzo często.',
        required: true,
        min: 1,
        max: 10,
        step: 1,
        marks: [
            { value: 1, label: '1' },
            { value: 5, label: '5' },
            { value: 10, label: '10' }
        ]
    },
    // Pytanie 2 - Część 2: Rozwój osobisty
    {
        id: 'personal_development',
        type: 'slider',
        question: 'Rozwój osobisty to wszystko, co robisz, aby uczyć się nowych rzeczy, rozwijać swoje umiejętności i lepiej poznawać siebie.',
        description: 'Oceń na skali 1–10 swój rozwój osobisty, gdzie 1 oznacza nie zajmuję się nim wcale, a 10 rozwijam się bardzo intensywnie.',
        required: true,
        min: 1,
        max: 10,
        step: 1,
        marks: [
            { value: 1, label: '1' },
            { value: 5, label: '5' },
            { value: 10, label: '10' }
        ]
    },
    // Pytanie 2 - Część 3: Zadowolenie z życia
    {
        id: 'life_satisfaction',
        type: 'slider',
        question: 'Zadowolenie z życia to ogólne poczucie, jak bardzo cieszysz się swoim życiem i codziennością.',
        description: 'Oceń na skali 1–10 swoje zadowolenie z życia, gdzie 1 oznacza bardzo niskie, a 10 bardzo wysokie.',
        required: true,
        min: 1,
        max: 10,
        step: 1,
        marks: [
            { value: 1, label: '1' },
            { value: 5, label: '5' },
            { value: 10, label: '10' }
        ]
    },
    // Pytanie 3 - Część 1: Czy doświadczasz trudności
    {
        id: 'experiencing_difficulties',
        type: 'radio',
        question: 'Czy doświadczasz trudności w życiu osobistym?',
        required: true,
        options: [
            { value: 'yes', label: 'TAK' },
            { value: 'no', label: 'NIE' }
        ]
    },
    // Pytanie 3 - Część 2: W jakich obszarach (warunkowe)
    {
        id: 'difficulty_areas',
        type: 'multiselect',
        question: 'W jakich obszarach życia dostrzegasz trudności?',
        description: '(wybierz maksymalnie 5 obszarów)',
        required: true,
        maxSelections: 5,
        conditional: {
            dependsOn: 'experiencing_difficulties',
            showWhen: 'yes'
        },
        options: [
            { value: 'work_difficulties', label: 'Trudności w życiu zawodowym' },
            { value: 'relationships', label: 'Relacje' },
            { value: 'habit_change', label: 'Zmiana nawyków' },
            { value: 'personal_development_diff', label: 'Rozwój osobisty' },
            { value: 'emotion_management', label: 'Zarządzanie emocjami i nastrojem' },
            { value: 'financial_management', label: 'Zarządzanie finansami i stresem finansowym' },
            { value: 'parenting', label: 'Rodzicielstwo i opieka' }
        ]
    },
    // Pytanie 4: Czas dostępu do specjalisty
    {
        id: 'access_time_important',
        type: 'radio',
        question: 'Czy w razie potrzeby, czas dostępu do specjalisty byłby dla Ciebie istotny?',
        required: true,
        options: [
            { value: 'asap', label: 'Tak - najchętniej jak najszybciej' },
            { value: '1-2weeks', label: 'Tak - w ciągu 1-2 tygodni' },
            { value: 'no', label: 'Nie - chętnie dostosuję się do grafiku specjalisty' }
        ]
    },
    // Pytanie 5: W jaki sposób rozwiązujesz trudności
    {
        id: 'current_solutions',
        type: 'multiselect',
        question: 'W jaki sposób zwykle rozwiązujesz takie trudności?',
        required: true,
        options: [
            { value: 'therapy_coaching', label: 'Terapia/coaching' },
            { value: 'webinars', label: 'Webinary' },
            { value: 'friends_talks', label: 'Rozmowy z przyjaciółmi' },
            { value: 'books', label: 'Książki tematyczne' },
            { value: 'not_solving', label: 'Nie rozwiązuje' },
            { value: 'other', label: 'Inne …' },
        ]
    },
    // Pytanie 6: Czy to wystarcza
    {
        id: 'solutions_sufficient',
        type: 'radio',
        question: 'Czy to wystarcza?',
        required: true,
        options: [
            { value: 'yes', label: 'Tak' },
            { value: 'no', label: 'Nie' }
        ]
    },
    // Pytanie 7: Czy cena jest istotna
    {
        id: 'price_important',
        type: 'radio',
        question: 'Czy cena jest istotnym kryterium korzystania ze wsparcia specjalisty?',
        required: true,
        options: [
            { value: 'yes', label: 'Tak' },
            { value: 'no', label: 'Nie' }
        ]
    },
    // Pytanie 8: Czy zainteresowany innymi formami pomocy
    {
        id: 'interested_other_help',
        type: 'radio',
        question: 'Czy jesteś zainteresowany znalezieniem innych form pomocy w radzeniu sobie z trudnościami (np. dostęp do aplikacji wspierającej dobrostan)',
        required: true,
        options: [
            { value: 'yes', label: 'Tak' },
            { value: 'no', label: 'Nie' }
        ]
    },
    // Pytanie 10: Czego oczekujesz od aplikacji
    {
        id: 'app_expectations',
        type: 'multiselect',
        question: 'Czego oczekiwałbyś od aplikacji wspierającej rozwój osobisty?',
        description: '(wybierz 5 najważniejszych dla Ciebie)',
        required: true,
        maxSelections: 5,
        options: [
            { value: 'data_security', label: 'Bezpieczeństwo informacji' },
            { value: 'immediate_access', label: 'Natychmiastowy dostęp' },
            { value: 'affordable_price', label: 'Przystępna cena' },
            { value: 'life_threat_monitoring', label: 'Monitorowanie zagrożenia życia' },
            { value: 'anonymity', label: 'Anonimowość' },
            { value: 'individual_solutions', label: 'Indywidualne rozwiązania' },
            { value: 'user_friendly_language', label: 'Język dostosowany do użytkownika' },
            { value: 'intuitive_interface', label: 'Intuicyjny interfejs' },
            { value: 'other', label: 'Inne,jakie:...' }
        ]
    },
    // Pytanie 11: Media
    {
        id: 'media_usage',
        type: 'multiselect',
        question: 'Zaznacz media, z których korzystasz najczęściej:',
        required: true,
        options: [
            { value: 'facebook', label: 'Facebook' },
            { value: 'instagram', label: 'Instagram' },
            { value: 'tiktok', label: 'TikTok' },
            { value: 'news_portals', label: 'Portale informacyjne' },
            { value: 'podcasts_audiobooks', label: 'Podcasty i audiobooki' },
            { value: 'television', label: 'Telewizja' },
            { value: 'streaming', label: 'Streamingi (np. Netflix)' },
            { value: 'radio', label: 'Radio' },
            { value: 'forums_groups', label: 'Fora i grupy dyskusyjne' },
            { value: 'other_media', label: 'Inne (jakie:.....)' }
        ]
    },
    // Pytanie 12: Czy chciałbyś użyć aplikacji testowej teraz
    {
        id: 'want_to_use_app_now',
        type: 'radio',
        question: 'Czy chciałbyś użyć aplikacji testowej teraz?',
        required: true,
        options: [
            { value: 'yes', label: 'Tak' },
            { value: 'no', label: 'Nie' }
        ]
    }
];

const QuestionnairePage = () => {
    const navigate = useNavigate();
    const { completePreQuestionnaire } = useQuestionnaireContext();
    const [showThankYouModal, setShowThankYouModal] = useState(false);

    // Redirect if questionnaire already completed


    const handleQuestionnaireComplete = (answers) => {
        console.log('Pre-questionnaire completed:', answers);
        completePreQuestionnaire(answers);

        // Check if user wants to use the app now
        if (answers.want_to_use_app_now === 'no') {
            // Show thank you modal if user answered "no"
            setShowThankYouModal(true);
        } else {
            // Redirect to the app page if user answered "yes"
            navigate('/zmiana-pracy');
        }
    };

    const handleCloseModal = () => {
        setShowThankYouModal(false);
        navigate('/');
    };

    const handleBack = () => {
        navigate('/');
    };

    return (
        <>
            <Questionnaire
                title=""
                description="Jesteśmy startupem tworzącym innowacyjne narzędzie do rozwoju zawodowego i osobistego. Nasza aplikacja pozwala odkryć mocne strony, planować działania w obszarach kariery, rozwoju osobistego, relacji, budowania nawyków i jest atrakcyjną cenowo alternatywą dla spotkania ze specjalistą. Dla potrzeb procesu inkubacji pomysłu prosimy o wypełnienie krótkiej ankiety – pomoże nam to lepiej dopasować aplikację do potrzeb użytkowników."
                questions={samplePreQuestionnaire}
                onComplete={handleQuestionnaireComplete}
                onBack={handleBack}
                showProgress={true}
                submitButtonText="Zakończ ankietę"
            />

            {/* Thank You Modal */}
            <Dialog
                open={showThankYouModal}
                onClose={handleCloseModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        p: 2
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CheckCircleIcon
                            sx={{
                                fontSize: 60,
                                color: 'success.main',
                                mb: 1
                            }}
                        />
                        <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', color: 'grey.800' }}>
                            Dziękujemy!
                        </Typography>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: 'grey.700' }}>
                        Bardzo dziękujemy za wypełnienie ankiety!
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'grey.600', lineHeight: 1.6 }}>
                        Twoje odpowiedzi są dla nas bardzo cenne i pomogą nam w dalszym rozwoju aplikacji.
                        Dzięki Twojej opinii możemy tworzyć jeszcze lepsze narzędzia wspierające rozwój osobisty.
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleCloseModal}
                        size="large"
                        sx={{
                            background: 'linear-gradient(90deg, #c79a5f, #9b7b5e)',
                            px: 4,
                            py: 1.5,
                            borderRadius: 2,
                            fontWeight: 'bold',
                            '&:hover': {
                                background: 'linear-gradient(90deg, #c79a5f, #9b7b5e)',
                                transform: 'scale(1.05)'
                            }
                        }}
                    >
                        Powrót do strony głównej
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default QuestionnairePage;
